/*============================================================================
  HC - IB mobile menu v2 controller - 2026-09-19

  Drives snippets/ib-mobile-menu.liquid. Loaded from layout/theme.liquid only
  when settings.mobile_menu_v2 is on, after theme.js and hc-custom.js.

  What it does:
  - Portals the root out of the header section to <body> at boot. The header
    section is a position:sticky; z-index:6 stacking context, so a fixed
    overlay left inside it would sit under the cart drawer (20000) and every
    other body-level surface. Region hiding still finds the brand links
    because assets/ib-brand-menu-regions.js scopes to [data-ib-mobile-menu].
  - Owns open/close and keeps the stock drawer's contracts: 'MobileNav:open' /
    'MobileNav:close' on document, 'html.mobile-nav-open', '.mobile-nav-trigger
    .is-active'. Closes on 'CartDrawer:open' and 'unmatchSmall' like stock did.
  - Runs the panel stack (root -> level 2 -> level 3) with 'inert' on every
    panel that is not on top.
  - Builds the flat A-Z brand index at boot (sort + letter headers happen here,
    not in Liquid, because 'sort_natural' is not accent-aware) and drives the
    sticky brand filter. Rows the region script hid are never counted.

  Everything the tests need is exported on window.IBMobileMenu: the pure
  helpers and the live instance. ES5 on purpose (no build step).
==============================================================================*/
(function () {
  'use strict';

  var SELECTORS = {
    root: '[data-ib-mobile-menu]',
    trigger: '.mobile-nav-trigger',
    closeBtn: '[data-ib-mm-close]',
    stage: '[data-ib-mm-stage]',
    panel: '[data-ib-mm-panel]',
    target: '[data-ib-mm-target]',
    back: '[data-ib-mm-back]',
    heading: '[data-ib-mm-heading]',
    brandsPanel: '[data-ib-mm-brands]',
    brandSearch: '[data-ib-mm-brand-search]',
    brandClear: '[data-ib-mm-brand-clear]',
    brandCount: '[data-ib-mm-brand-count]',
    brandSource: '[data-ib-mm-brand-source]',
    brandIndex: '[data-ib-mm-brand-index]',
    brandRow: '[data-ib-mm-brand]',
    brandList: '[data-ib-mm-grid]',
    group: '[data-ib-mm-group]',
    letter: '[data-ib-mm-letter]',
    rail: '[data-ib-mm-rail]',
    railBtn: '[data-ib-mm-rail-letter]',
    head: '.ib-mm__head',
    empty: '[data-ib-mm-empty]',
    focusable: 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
  };

  var NAMESPACE = '.ib-mobile-menu';
  var ROOT_PANEL = 'root';
  var TRANSITION_MS = 320;      // CSS panel/overlay transition is .3s; a little slack
  var FILTER_DEBOUNCE_MS = 80;
  var REGION_HIDDEN_ATTR = 'data-ib-region-hidden';
  var ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  var LIVE_BRAND_ROW = '[data-ib-mm-brand]:not([hidden]):not([data-ib-region-hidden])';
  // Rail scrubbing has to be able to preventDefault, so it cannot be passive.
  var TOUCH_OPTS = { passive: false };
  // Travel before a touch on the rail counts as a drag rather than a tap.
  var SCRUB_SLOP = 6;
  // How long a tap may look like it is loading while this page is still here.
  // Past this something stopped the navigation without telling us - a Back or
  // Stop before the next page arrived, a 204, a file download, an app link -
  // and a menu stuck dimmed would look frozen.
  var CHOSEN_MAX_MS = 10000;
  // How long the menu holds a tapped link before loading its page, so the
  // spotlight is painted first (see takeOverLink). The theme's own page
  // transitions hold for 50ms for the same reason.
  var NAV_HOLD_MS = 70;
  // How long the rail stays awake after the list stops moving.
  var RAIL_WAKE_MS = 1100;
  // One-time nudge: a pause so the panel's slide lands first, then one quick
  // sweep down the rail - 27 letters at this beat is about three quarters of a
  // second, fast enough to read as a single stroke rather than a walk.
  var NUDGE_DELAY_MS = 550;
  var NUDGE_STEP_MS = 28;
  var NUDGE_KEY = 'ib-mm-rail-nudge';

  /* ------------------------------------------------------------------------
     Pure helpers (exported for tests)
  ------------------------------------------------------------------------ */

  // "L'Oreal" -> "loreal", "äz" -> "az", "milk_shake" -> "milkshake",
  // "It's a 10" -> "its a 10". Diacritics are stripped via NFD when available.
  function normalizeText(value) {
    var s = value === null || value === undefined ? '' : String(value);
    if (typeof s.normalize === 'function') {
      s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }
    s = s.toLowerCase();
    s = s.replace(/&amp;/g, ' ');
    s = s.replace(/[^a-z0-9 ]+/g, '');
    s = s.replace(/\s+/g, ' ').replace(/^ | $/g, '');
    return s;
  }

  function brandSortKey(title) {
    return normalizeText(title);
  }

  function letterFor(key) {
    var c = String(key || '').charAt(0);
    return /[a-z]/.test(c) ? c.toUpperCase() : '#';
  }

  // Both arguments are already normalized. Empty query matches everything.
  function matchesQuery(normTitle, normQuery) {
    var title = String(normTitle || '');
    var query = String(normQuery || '');
    if (!query) return true;
    if (title.indexOf(query) !== -1) return true;
    var squashedTitle = title.replace(/ /g, '');
    var squashedQuery = query.replace(/ /g, '');
    if (squashedQuery && squashedTitle.indexOf(squashedQuery) !== -1) return true;
    var words = title.split(' ');
    for (var i = 0; i < words.length; i++) {
      if (words[i].indexOf(query) === 0) return true;
    }
    return false;
  }

  function compareKeys(a, b) {
    var la = letterFor(a);
    var lb = letterFor(b);
    if (la === '#' && lb !== '#') return 1;
    if (lb === '#' && la !== '#') return -1;
    if (typeof a.localeCompare === 'function') {
      try {
        return a.localeCompare(b, 'en', { sensitivity: 'base', numeric: true });
      } catch (e) {
        /* fall through */
      }
    }
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
  }

  // items: [{ title, href, key?, el? }]. Returns a new array, sorted by key
  // ('#' bucket last) and deduped by href (first occurrence wins). Stable.
  function sortBrands(items) {
    var seen = {};
    var out = [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (!item) continue;
      var href = item.href || '';
      if (href && seen[href]) continue;
      if (href) seen[href] = true;
      var key = item.key || brandSortKey(item.title);
      out.push({ item: item, key: key, index: i });
    }
    out.sort(function (a, b) {
      var c = compareKeys(a.key, b.key);
      return c !== 0 ? c : a.index - b.index;
    });
    var result = [];
    for (var j = 0; j < out.length; j++) {
      var entry = out[j].item;
      entry.key = out[j].key;
      result.push(entry);
    }
    return result;
  }

  // sorted: output of sortBrands. Returns [{ letter, items }], '#' last.
  function groupByLetter(sorted) {
    var groups = [];
    var byLetter = {};
    for (var i = 0; i < sorted.length; i++) {
      var item = sorted[i];
      var letter = letterFor(item.key || brandSortKey(item.title));
      if (!byLetter[letter]) {
        byLetter[letter] = { letter: letter, items: [] };
        groups.push(byLetter[letter]);
      }
      byLetter[letter].items.push(item);
    }
    groups.sort(function (a, b) {
      if (a.letter === '#') return 1;
      if (b.letter === '#') return -1;
      return a.letter < b.letter ? -1 : a.letter > b.letter ? 1 : 0;
    });
    return groups;
  }

  /* ------------------------------------------------------------------------
     Small DOM utilities
  ------------------------------------------------------------------------ */

  function toArray(list) {
    var out = [];
    if (!list) return out;
    for (var i = 0; i < list.length; i++) out.push(list[i]);
    return out;
  }

  function emit(type, detail) {
    var evt;
    try {
      evt = new CustomEvent(type, { detail: detail || null });
    } catch (e) {
      return;
    }
    document.dispatchEvent(evt);
  }

  function setInert(panel, inert) {
    if (!panel) return;
    if (inert) {
      panel.setAttribute('inert', '');
      panel.setAttribute('aria-hidden', 'true');
    } else {
      panel.removeAttribute('inert');
      panel.removeAttribute('aria-hidden');
    }
  }

  /*
    Two different failures, two different answers. A read that throws (Safari
    private browsing) or missing storage counts as "already seen": we cannot
    tell, so we do not pester. A write that throws after a good read (a full
    quota) still shows the nudge once - nudgeShownHere is what stops it from
    running again on every visit to the panel.
  */
  var nudgeShownHere = false;

  function nudgeSeen() {
    if (nudgeShownHere) return true;
    try {
      return !window.localStorage || window.localStorage.getItem(NUDGE_KEY) === '1';
    } catch (e) {
      return true;
    }
  }

  function markNudgeSeen() {
    nudgeShownHere = true;
    try {
      if (window.localStorage) window.localStorage.setItem(NUDGE_KEY, '1');
    } catch (e) {
      /* remembered for this page at least */
    }
  }
  /*
    Whether this click is about to replace the page. Only then is there a wait
    worth showing: anything else would leave the tap mark and the loading line
    up with nothing coming to take them down. Hash-only hrefs are handled
    before this is asked.
  */
  function isMarkable(link) {
    return Boolean(link.classList) &&
      (link.classList.contains('ib-mm__link') || link.classList.contains('ib-mm__shopall'));
  }

  function leavesThisPage(evt, link) {
    // A new tab or window. (Other mouse buttons fire auxclick, not click.)
    if (evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return false;
    var target = link.getAttribute('target');
    if (target && !/^_(self|top|parent)$/i.test(target)) return false;
    if (link.hasAttribute('download')) return false;

    // tel:, mailto:, sms: go to another app and javascript: goes nowhere. The
    // resolved protocol, not the raw href: the browser trims ' tel:...' and
    // dials it all the same.
    var protocol = typeof link.protocol === 'string' ? link.protocol : '';
    if (!protocol) {
      var scheme = /^\s*([a-z][a-z0-9+.-]*:)/i.exec(link.getAttribute('href') || '');
      protocol = scheme ? scheme[1] : '';
    }
    if (protocol && !/^https?:$/i.test(protocol)) return false;

    // This same page with a fragment only scrolls.
    var url = typeof link.href === 'string' ? link.href : '';
    var here = window.location && typeof window.location.href === 'string' ? window.location.href : '';
    if (here && url.indexOf('#') !== -1 && url.split('#')[0] === here.split('#')[0]) return false;

    return true;
  }


  function prefersReducedMotion() {
    if (typeof window.matchMedia !== 'function') return false;
    try {
      return Boolean(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    } catch (e) {
      return false;
    }
  }

  function focusNoScroll(el) {
    if (!el || typeof el.focus !== 'function') return;
    try {
      el.focus({ preventScroll: true });
    } catch (e) {
      try {
        el.focus();
      } catch (e2) {
        /* ignore */
      }
    }
  }

  function hasThemeA11y() {
    return typeof window.theme === 'object' && window.theme && window.theme.a11y;
  }

  /*
    A control the focus trap may hand focus to. On top of the [hidden] and
    region-hidden checks this drops <input type="hidden"> and anything hidden
    by CSS: display:none on the element or an ancestor yields no client rects,
    and visibility:hidden keeps rects but cannot take focus. The footer menus
    theme.js clones into #MobileNavFooter carry both (the localization form's
    hidden inputs and its collapsed disclosure lists), and without this the
    trap would pick an unfocusable "last" control and Tab would escape.
  */
  function isFocusTarget(el) {
    if (!el || el.hidden) return false;
    if (typeof el.closest === 'function' && el.closest('[hidden]')) return false;
    if (el.getAttribute && el.getAttribute(REGION_HIDDEN_ATTR)) return false;
    var tag = el.tagName ? String(el.tagName).toLowerCase() : '';
    if (tag === 'input' && el.getAttribute && String(el.getAttribute('type') || '').toLowerCase() === 'hidden') return false;
    if (typeof el.getClientRects === 'function' && el.getClientRects().length === 0) return false;
    if (typeof window.getComputedStyle === 'function') {
      try {
        if (window.getComputedStyle(el).visibility === 'hidden') return false;
      } catch (e) {
        /* ignore */
      }
    }
    return true;
  }

  /* ------------------------------------------------------------------------
     Controller
  ------------------------------------------------------------------------ */

  function IBMobileMenu(root) {
    this.root = root;
    this.isOpen = false;
    this.stack = [ROOT_PANEL];
    this.panels = {};
    this.lastFocus = null;
    this.closeTimer = null;
    this.filterTimer = null;
    this.listeners = [];
    this.destroyed = false;
    this.brandRows = [];
    this.brandGroups = [];
    this.brandLetters = [];
    this.triggers = [];

    this.closeBtn = root.querySelector(SELECTORS.closeBtn);
    this.stage = root.querySelector(SELECTORS.stage);
    this.brandsPanel = root.querySelector(SELECTORS.brandsPanel);
    this.rail = this.brandsPanel ? this.brandsPanel.querySelector(SELECTORS.rail) : null;
    this.railBtns = [];
    this.scrubLetter = '';
    this.railWakeTimer = null;
    this.nudgeTimer = null;
    this.nudging = false;
    this.skipScrollWake = false;
    this.chosenLink = null;
    this.chosenTimer = null;
    this.navTimer = null;
    this.brandInput = root.querySelector(SELECTORS.brandSearch);
    this.brandClear = root.querySelector(SELECTORS.brandClear);
    this.brandCount = root.querySelector(SELECTORS.brandCount);
    this.brandEmpty = root.querySelector(SELECTORS.empty);

    this.init();
  }

  IBMobileMenu.prototype = {
    init: function () {
      this.portal();
      this.indexPanels();
      this.buildBrandIndex();
      this.bindTriggers();
      this.bindPanels();
      this.bindBrandFilter();
      this.bindBrandScroll();
      this.bindRailScrub();
      this.bindKeys();
      this.bindGlobal();
    },

    /*
      Move the root to <body>. The header section is a stacking context capped
      at z-index 6, so a fixed overlay left in place would render under the
      cart drawer and every other body-level surface. Moving a node keeps its
      listeners, and <use href="#…"> resolves document-wide, so the sprite that
      travels with the root keeps working. Stale portaled roots (theme editor
      re-rendering the header section) are removed first so there is only
      ever one.
    */
    portal: function () {
      var body = document.body;
      if (!body || typeof body.appendChild !== 'function') return;
      var stale = toArray(body.querySelectorAll(SELECTORS.root + '[data-ib-mm-portaled]'));
      for (var i = 0; i < stale.length; i++) {
        if (stale[i] !== this.root && stale[i].parentNode && typeof stale[i].parentNode.removeChild === 'function') {
          stale[i].parentNode.removeChild(stale[i]);
        }
      }
      this.root.setAttribute('data-ib-mm-portaled', '1');
      body.appendChild(this.root);
    },

    indexPanels: function () {
      var panels = toArray(this.root.querySelectorAll(SELECTORS.panel));
      for (var i = 0; i < panels.length; i++) {
        var id = panels[i].getAttribute('data-ib-mm-panel');
        if (!id) continue;
        this.panels[id] = panels[i];
        if (id === ROOT_PANEL) {
          panels[i].classList.add('is-active');
          setInert(panels[i], false);
        } else {
          panels[i].classList.remove('is-active');
          panels[i].classList.remove('is-parent');
          setInert(panels[i], true);
        }
      }
    },

    current: function () {
      return this.stack[this.stack.length - 1];
    },

    /* ---------------- open / close ---------------- */

    open: function (evt) {
      if (evt && typeof evt.preventDefault === 'function') evt.preventDefault();
      if (this.isOpen) return;

      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }

      // Remember where focus was so close() can hand it back. Safari does not
      // focus a tapped <button>, so activeElement is often <body>; treat that
      // as "nowhere" so restoreFocus() falls back to the hamburger.
      var active = document.activeElement || null;
      if (active === document.body || active === document.documentElement) active = null;
      this.lastFocus = active;
      this.root.hidden = false;
      // Force a style flush so the opacity transition runs from the hidden state.
      void this.root.offsetWidth;
      this.root.classList.add('is-open');

      this.setTriggersActive(true);
      document.documentElement.classList.add('mobile-nav-open');
      this.lockScrolling();

      this.isOpen = true;
      emit('MobileNav:open');

      // The head's height (and so where sticky letter headers park) is only
      // measurable once the menu is actually laid out.
      this.syncHeadOffset();
      this.focusInto(this.closeBtn);
    },

    /*
      close(immediate, options)
        immediate      skip the fade and hide the root now
        options.keepFocus  do not move focus back to the hamburger. Used when a
                       stock modal (theme.Modals, e.g. the timed newsletter
                       popup) has just opened and already owns focus: the
                       menu sits at z-index 19000, well above the modal's 30,
                       so it must get out of the way without stealing the
                       modal's focus back.
    */
    close: function (immediate, options) {
      if (!this.isOpen) return;
      var keepFocus = Boolean(options && options.keepFocus);

      this.root.classList.remove('is-open');
      this.setTriggersActive(false);
      document.documentElement.classList.remove('mobile-nav-open');
      this.unlockScrolling();

      this.isOpen = false;
      this.settleRail();
      this.clearChosen();
      emit('MobileNav:close');

      var self = this;
      var finish = function () {
        self.closeTimer = null;
        self.root.hidden = true;
        self.resetToRoot();
      };

      if (immediate === true || prefersReducedMotion()) {
        finish();
      } else {
        this.closeTimer = setTimeout(finish, TRANSITION_MS);
      }

      if (keepFocus) {
        this.lastFocus = null;
      } else {
        this.restoreFocus();
      }
    },

    toggle: function (evt) {
      if (this.isOpen) {
        this.close();
      } else {
        this.open(evt);
      }
    },

    setTriggersActive: function (active) {
      for (var i = 0; i < this.triggers.length; i++) {
        var btn = this.triggers[i];
        if (active) {
          btn.classList.add('is-active');
        } else {
          btn.classList.remove('is-active');
        }
        btn.setAttribute('aria-expanded', active ? 'true' : 'false');
      }
    },

    lockScrolling: function () {
      if (hasThemeA11y() && typeof window.theme.a11y.lockMobileScrolling === 'function') {
        window.theme.a11y.lockMobileScrolling(NAMESPACE);
      }
    },

    unlockScrolling: function () {
      if (hasThemeA11y() && typeof window.theme.a11y.unlockMobileScrolling === 'function') {
        window.theme.a11y.unlockMobileScrolling(NAMESPACE);
      }
    },

    /* ---------------- panel stack ---------------- */

    goTo: function (id) {
      var next = this.panels[id];
      if (!next || id === this.current()) return;

      var cur = this.panels[this.current()];
      if (cur) {
        cur.classList.remove('is-active');
        cur.classList.add('is-parent');
        setInert(cur, true);
      }

      if (next.scrollTop) {
        next.scrollTop = 0;
        // The reset fires a scroll event a frame later. It is not the shopper
        // moving the list, so it must neither wake the rail nor cancel a nudge.
        if (next === this.brandsPanel) this.skipScrollWake = true;
      }
      next.classList.remove('is-parent');
      next.classList.add('is-active');
      setInert(next, false);

      this.stack.push(id);
      if (next === this.brandsPanel) {
        this.syncHeadOffset();
        // The reset's scroll event arrives a frame late and is skipped for
        // waking, so sync now rather than leave the thumb on last visit's
        // letter meanwhile. This is also the only repair for a highlight
        // stranded while the panel had no height to measure.
        this.syncCurrentLetterFromScroll();
        this.maybeNudgeRail();
      }
      this.focusInto(this.panelHeading(next));
    },

    back: function () {
      if (this.stack.length < 2) return;

      var leavingId = this.stack.pop();
      var leaving = this.panels[leavingId];
      var target = this.panels[this.current()];

      if (leaving && leaving === this.brandsPanel) this.settleRail();

      if (leaving) {
        leaving.classList.remove('is-active');
        leaving.classList.remove('is-parent');
        setInert(leaving, true);
      }
      if (target) {
        target.classList.remove('is-parent');
        target.classList.add('is-active');
        setInert(target, false);
        var opener = target.querySelector('[data-ib-mm-target="' + leavingId + '"]');
        this.focusInto(opener || this.panelHeading(target));
      }
    },

    // Where focus lands when a panel slides in: its heading (tabindex=-1) so
    // assistive tech announces the new level; the panel's back chevron is the
    // fallback.
    panelHeading: function (panel) {
      if (!panel) return null;
      return panel.querySelector(SELECTORS.heading) || panel.querySelector(SELECTORS.back);
    },

    resetToRoot: function () {
      this.stack = [ROOT_PANEL];
      for (var id in this.panels) {
        if (!Object.prototype.hasOwnProperty.call(this.panels, id)) continue;
        var panel = this.panels[id];
        panel.classList.remove('is-parent');
        panel.scrollTop = 0;
        if (id === ROOT_PANEL) {
          panel.classList.add('is-active');
          setInert(panel, false);
        } else {
          panel.classList.remove('is-active');
          setInert(panel, true);
        }
      }
      this.clearBrandFilter(false);
    },

    /* ---------------- bindings ---------------- */

    // Every document/window/trigger listener goes through here so destroy()
    // can take them all off again. Root-scoped listeners die with the root.
    listen: function (target, type, fn) {
      if (!target || typeof target.addEventListener !== 'function') return;
      target.addEventListener(type, fn);
      this.listeners.push({ target: target, type: type, fn: fn });
    },

    /*
      Theme editor: a header re-render produces a fresh root while this
      instance still owns document/window listeners and possibly the open
      state. boot() calls this before constructing the replacement so the
      preview never ends up scroll-locked with no visible dialog, and the old
      listeners cannot reopen a detached root.
    */
    destroy: function () {
      if (this.destroyed) return;
      this.destroyed = true;

      this.settleRail();
      this.clearChosen();
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
      if (this.filterTimer) {
        clearTimeout(this.filterTimer);
        this.filterTimer = null;
      }

      if (this.isOpen) {
        this.isOpen = false;
        this.setTriggersActive(false);
        document.documentElement.classList.remove('mobile-nav-open');
        this.unlockScrolling();
        emit('MobileNav:close');
      }

      for (var i = 0; i < this.listeners.length; i++) {
        var entry = this.listeners[i];
        if (typeof entry.target.removeEventListener === 'function') {
          entry.target.removeEventListener(entry.type, entry.fn);
        }
      }
      this.listeners = [];

      this.root.hidden = true;
      if (this.root.parentNode && typeof this.root.parentNode.removeChild === 'function') {
        this.root.parentNode.removeChild(this.root);
      }
    },

    bindTriggers: function () {
      var self = this;
      this.triggers = toArray(document.querySelectorAll(SELECTORS.trigger));
      for (var i = 0; i < this.triggers.length; i++) {
        this.triggers[i].setAttribute('aria-expanded', 'false');
        this.listen(this.triggers[i], 'click', function (evt) {
          self.toggle(evt);
        });
      }
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', function () {
          self.close();
        });
      }
    },

    /*
      One delegated listener on the root. Rows moved by buildBrandIndex and the
      footer menus theme.js clones into #MobileNavFooter need no rebinding.
      Real links are not closed: the page is about to unload, and closing
      first would flash the underlying page for the transition's duration.
      takeOverLink marks them instead, from window, so the wait for the next
      page shows. Hash links (the Swym wishlist) do not navigate, so those
      close the menu.
    */
    bindPanels: function () {
      var self = this;

      this.root.addEventListener('click', function (evt) {
        var el = evt.target;
        if (!el || typeof el.closest !== 'function') return;

        var target = el.closest(SELECTORS.target);
        if (target) {
          evt.preventDefault();
          self.goTo(target.getAttribute('data-ib-mm-target'));
          return;
        }

        var back = el.closest(SELECTORS.back);
        if (back) {
          evt.preventDefault();
          self.back();
          return;
        }

        var rail = el.closest(SELECTORS.railBtn);
        if (rail) {
          evt.preventDefault();
          // A mouse or pen click has no touchstart, and a jump to where the
          // list already is fires no scroll, so the nudge must be stopped here.
          self.endNudge();
          self.jumpToLetter(rail.getAttribute('data-ib-mm-rail-letter'));
          return;
        }

        var link = el.closest('a[href]');
        if (link && (link.getAttribute('href') || '').charAt(0) === '#') {
          self.close();
        }
        // Real page loads are taken over later, from window (takeOverLink).
      });
    },

    /*
      iOS Safari stops painting the moment a link starts loading the next page,
      and holds that frame until the new page paints. Anything drawn in answer
      to the tap itself is never seen - the shopper stares at an untouched menu.
      So the menu takes the navigation over: it marks the tap, lets a few frames
      paint it, then loads the page itself.

      Only for rows the stylesheet can show as chosen, only when nothing else
      cancelled the click, and not for _top/_parent (from inside the theme
      editor's frame, driving another browsing context can throw).
    */
    takeOverLink: function (evt) {
      if (!this.isOpen || !evt || evt.defaultPrevented) return;
      var el = evt.target;
      if (!el || typeof el.closest !== 'function') return;
      var link = el.closest('a[href]');
      if (!link || link.closest(SELECTORS.root) !== this.root) return;
      if ((link.getAttribute('href') || '').charAt(0) === '#') return;
      if (!isMarkable(link) || !leavesThisPage(evt, link)) return;
      var target = link.getAttribute('target');
      if (target && !/^_self$/i.test(target)) return;

      var url = typeof link.href === 'string' && link.href ? link.href : link.getAttribute('href');
      evt.preventDefault();
      this.markChosen(link);

      var self = this;
      if (this.navTimer) clearTimeout(this.navTimer);
      this.navTimer = setTimeout(function () {
        self.navTimer = null;
        if (self.chosenLink === link) window.location.href = url;
      }, NAV_HOLD_MS);
    },

    /*
      A tapped link that leaves the page. Nothing on screen changes until the
      next page paints, which on a phone can be a second or two, so until
      then the rest of the panel steps back and the chosen link shows it is
      loading (see the stylesheet).
    */
    markChosen: function (link) {
      // A tap during the close fade: the menu is on its way out, and a mark
      // set now would still be there to greet the next open.
      if (!this.isOpen || this.chosenLink === link) return;
      if (this.chosenLink) this.chosenLink.classList.remove('is-chosen');
      this.chosenLink = link;
      link.classList.add('is-chosen');
      // Left on, never toggled: the rest of the panel is already stepped back,
      // and switching it off and on would flash the whole list back in.
      this.root.classList.add('is-navigating');

      var self = this;
      if (this.chosenTimer) clearTimeout(this.chosenTimer);
      this.chosenTimer = setTimeout(function () {
        self.chosenTimer = null;
        self.clearChosen();
      }, CHOSEN_MAX_MS);
    },

    // Nothing is loading any more: the menu was put away - which is also how
    // a page the back button restores from the cache comes back - the shopper
    // came back from another app, or the wait simply ran out.
    clearChosen: function () {
      // Closing the menu inside the hold is a change of mind: no page load.
      if (this.navTimer) {
        clearTimeout(this.navTimer);
        this.navTimer = null;
      }
      if (this.chosenTimer) {
        clearTimeout(this.chosenTimer);
        this.chosenTimer = null;
      }
      if (this.chosenLink) {
        this.chosenLink.classList.remove('is-chosen');
        this.chosenLink = null;
      }
      if (this.root && this.root.classList) this.root.classList.remove('is-navigating');
    },

    bindKeys: function () {
      var self = this;
      this.listen(document, 'keydown', function (evt) {
        if (!self.isOpen) return;
        var key = evt.key || (evt.keyCode === 27 ? 'Escape' : evt.keyCode === 9 ? 'Tab' : '');

        if (key === 'Escape' || key === 'Esc') {
          if (self.brandInput && document.activeElement === self.brandInput && self.brandInput.value) {
            evt.preventDefault();
            self.clearBrandFilter(true);
            return;
          }
          self.close();
          return;
        }

        if (key === 'Tab') {
          self.trapTab(evt);
        }
      });
    },

    bindGlobal: function () {
      var self = this;

      this.listen(document, 'CartDrawer:open', function () {
        self.close();
      });
      this.listen(document, 'unmatchSmall', function () {
        self.close(true);
      });
      // Any stock modal opening (timed newsletter popup, quick shop, video):
      // same close-on-conflict wiring the header cart and cart drawer v2 use.
      // The modal already trapped focus on itself, so keep it there.
      this.listen(document, 'modalOpen', function () {
        self.close(true, { keepFocus: true });
      });

      // Stock dev hooks kept for parity with theme.MobileNav.
      this.listen(document, 'mobileNav:open', function (evt) {
        self.open(evt);
      });
      this.listen(document, 'mobileNav:close', function () {
        self.close();
      });

      // bfcache: a page restored from the back/forward cache must never come
      // back with the menu open and the html class stuck on.
      this.listen(window, 'resize', function () {
        if (!self.isOpen) return; // nothing to measure while the menu is closed
        self.syncHeadOffset();
      });

      // Back from an app the link handed off to (a universal link, a file
      // viewer): this page never went anywhere, so nothing is loading.
      // Last in the click's path, so anything that cancels it - an app, a
      // delegated handler on document or body - has already had its say.
      this.listen(window, 'click', function (evt) {
        self.takeOverLink(evt);
      });

      this.listen(document, 'visibilitychange', function () {
        if (document.visibilityState === 'visible' && self.chosenLink) self.clearChosen();
      });

      this.listen(window, 'pageshow', function (evt) {
        if (evt && evt.persisted) self.close(true);
      });

      // Theme editor: the header section re-renders on every setting change,
      // producing a fresh root inside the header while the old one is still
      // portaled on <body>. Re-boot on the new one; portal() removes the old.
      if (window.Shopify && window.Shopify.designMode) {
        this.listen(document, 'shopify:section:load', function (evt) {
          var section = evt && evt.target;
          if (section && typeof section.querySelector === 'function' && section.querySelector(SELECTORS.root)) {
            boot();
          }
        });
      }
    },

    /* ---------------- focus ---------------- */

    /*
      Focus without scrolling. The stage is overflow:hidden and the panel that
      is sliding in is still translated off to the right when this runs; a
      plain focus() would make the browser scroll the stage sideways to reveal
      the control, shifting every panel and leaving the visible one blank.
      preventScroll stops that, and the stage scroll is reset afterwards for
      engines that ignore the option.
    */
    focusInto: function (el) {
      var self = this;
      if (!el || typeof el.focus !== 'function') return;
      setTimeout(function () {
        focusNoScroll(el);
        self.resetStageScroll();
      }, 0);
    },

    resetStageScroll: function () {
      if (!this.stage) return;
      if (this.stage.scrollLeft) this.stage.scrollLeft = 0;
      if (this.stage.scrollTop) this.stage.scrollTop = 0;
    },

    restoreFocus: function () {
      var target = this.lastFocus;
      this.lastFocus = null;
      if (!target && this.triggers.length) target = this.triggers[0];
      if (target && typeof target.focus === 'function') {
        try {
          target.focus();
        } catch (e) {
          /* ignore */
        }
      }
    },

    // Focusables inside the top bar and the active panel (its head carries the
    // back chevron). Stock theme.a11y.trapFocus keys off offsetParent, which is
    // non-null for the offscreen translated panels, so it would wrap into
    // invisible controls.
    focusables: function () {
      var scope = [];
      var bar = this.root.querySelector('.ib-mm__bar');
      var active = this.panels[this.current()];
      if (bar) scope = scope.concat(toArray(bar.querySelectorAll(SELECTORS.focusable)));
      if (active) scope = scope.concat(toArray(active.querySelectorAll(SELECTORS.focusable)));
      var out = [];
      for (var i = 0; i < scope.length; i++) {
        var el = scope[i];
        if (el.closest && el.closest(SELECTORS.rail)) continue; // pointer-only shortcut
        if (isFocusTarget(el)) out.push(el);
      }
      return out;
    },

    trapTab: function (evt) {
      var focusables = this.focusables();
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      var active = document.activeElement || null;
      var backwards = Boolean(evt.shiftKey);
      var at = focusables.indexOf(active);

      if (at === -1) {
        evt.preventDefault();
        focusNoScroll(backwards ? last : first);
        this.resetStageScroll();
        return;
      }
      if (!backwards && active === last) {
        evt.preventDefault();
        focusNoScroll(first);
        this.resetStageScroll();
        return;
      }
      if (backwards && active === first) {
        evt.preventDefault();
        focusNoScroll(last);
        this.resetStageScroll();
      }
    },

    /* ---------------- brands ---------------- */

    /*
      Reads the hidden source list Liquid emitted (menu order, deduped by URL),
      sorts it accent-aware, and MOVES the <li> nodes into letter sections.
      Moving rather than re-creating keeps whatever the region script already
      did to a row (inline display:none + data-ib-region-hidden), and rows it
      hides later are still found under [data-ib-mobile-menu].
    */
    buildBrandIndex: function () {
      if (!this.brandsPanel) return;

      var source = this.brandsPanel.querySelector(SELECTORS.brandSource);
      var index = this.brandsPanel.querySelector(SELECTORS.brandIndex);

      if (source && index) {
        var rows = toArray(source.querySelectorAll(SELECTORS.brandRow));
        var items = [];
        for (var i = 0; i < rows.length; i++) {
          var row = rows[i];
          var anchor = row.querySelector('a[href]');
          var title = row.getAttribute('data-ib-mm-title') || (anchor ? anchor.textContent : '') || '';
          items.push({
            el: row,
            title: title,
            href: anchor ? anchor.getAttribute('href') || '' : ''
          });
        }

        var groups = groupByLetter(sortBrands(items));
        for (var g = 0; g < groups.length; g++) {
          var group = groups[g];
          var section = document.createElement('section');
          section.className = 'ib-mm__letter';
          section.setAttribute('data-ib-mm-letter', group.letter);

          var head = document.createElement('h4');
          head.className = 'ib-mm__letter-head';
          head.textContent = group.letter;
          section.appendChild(head);

          var list = document.createElement('ul');
          list.className = 'ib-mm__list';
          list.setAttribute('data-ib-mm-grid', ''); // two-column grid: syncColumns tags its right column
          for (var k = 0; k < group.items.length; k++) {
            list.appendChild(group.items[k].el);
          }
          section.appendChild(list);
          index.appendChild(section);
        }
      }

      // Cache normalized keys on every brand row (Elite + index) once.
      this.brandRows = toArray(this.brandsPanel.querySelectorAll(SELECTORS.brandRow));
      for (var r = 0; r < this.brandRows.length; r++) {
        var el = this.brandRows[r];
        if (!el.getAttribute('data-ib-mm-key')) {
          el.setAttribute('data-ib-mm-key', brandSortKey(el.getAttribute('data-ib-mm-title') || el.textContent || ''));
        }
      }
      this.brandGroups = toArray(this.brandsPanel.querySelectorAll(SELECTORS.group));
      this.brandLetters = toArray(this.brandsPanel.querySelectorAll(SELECTORS.letter));
      this.buildRail();
      this.syncColumns();
      this.syncHeadOffset();
    },

    /*
      A-Z rail: one button per letter of the alphabet plus '#', always all of
      them, so a letter never moves under the thumb when a search empties it -
      empty letters are just disabled. Built here because the letter sections
      themselves only exist after the index build above.
    */
    buildRail: function () {
      if (!this.rail || !this.brandLetters || !this.brandLetters.length) return;

      var letters = ALPHABET.split('');
      letters.push('#');
      this.railBtns = [];

      for (var i = 0; i < letters.length; i++) {
        var btn = document.createElement('button');
        btn.className = 'ib-mm__rail-btn';
        btn.setAttribute('type', 'button');
        btn.setAttribute('tabindex', '-1');
        btn.setAttribute('data-ib-mm-rail-letter', letters[i]);
        btn.textContent = letters[i];
        this.rail.appendChild(btn);
        this.railBtns.push(btn);
      }

      this.rail.hidden = false;
      this.syncRail();
    },

    /*
      Marks the rows that land in the visual right-hand column, which needs a
      wider gutter because the jump rail floats over that edge. CSS cannot do
      this with :nth-child: searching hides rows, hidden rows still count as
      children, and the padding would end up on the wrong side of the grid -
      the same parity trap the centre rule avoids by being a gradient.
    */
    syncColumns: function () {
      if (!this.brandsPanel) return;
      var lists = toArray(this.brandsPanel.querySelectorAll(SELECTORS.brandList));

      for (var i = 0; i < lists.length; i++) {
        var rows = toArray(lists[i].querySelectorAll(SELECTORS.brandRow));
        var visible = 0;
        for (var j = 0; j < rows.length; j++) {
          var row = rows[j];
          if (row.hidden || row.getAttribute(REGION_HIDDEN_ATTR)) {
            row.removeAttribute('data-ib-mm-col');
            continue;
          }
          if (visible % 2 === 1) row.setAttribute('data-ib-mm-col', 'right');
          else row.removeAttribute('data-ib-mm-col');
          visible += 1;
        }
      }
    },

    // Vertical span of the letters themselves, inset from the track's own edges.
    railEdges: function (rect) {
      if (!rect) return { top: 0, bottom: 0 };
      var first = this.railBtns && this.railBtns[0];
      var last = this.railBtns && this.railBtns[this.railBtns.length - 1];
      var top = rect.top + 1;
      var bottom = rect.bottom - 1;

      if (first && typeof first.getBoundingClientRect === 'function') {
        var firstBox = first.getBoundingClientRect();
        if (firstBox && firstBox.height) top = firstBox.top + 1;
      }
      if (last && typeof last.getBoundingClientRect === 'function') {
        var lastBox = last.getBoundingClientRect();
        if (lastBox && lastBox.height) bottom = lastBox.bottom - 1;
      }
      return { top: top, bottom: bottom };
    },

    letterSection: function (letter) {
      if (!this.brandLetters) return null;
      for (var i = 0; i < this.brandLetters.length; i++) {
        if (this.brandLetters[i].getAttribute('data-ib-mm-letter') === letter) return this.brandLetters[i];
      }
      return null;
    },

    /*
      A rail letter is live when its section still holds a row the shopper can
      actually reach: not filtered out by the search (our [hidden]) and not
      collapsed by the region script. Asking the rows rather than the section's
      own hidden flag keeps the rail honest before the first filter pass runs.
    */
    syncRail: function () {
      if (!this.railBtns || !this.railBtns.length) return;
      for (var i = 0; i < this.railBtns.length; i++) {
        var btn = this.railBtns[i];
        var section = this.letterSection(btn.getAttribute('data-ib-mm-rail-letter'));
        btn.disabled = !section || !section.querySelector(LIVE_BRAND_ROW);
      }
    },

    setCurrentLetter: function (letter) {
      if (!this.railBtns) return;
      for (var i = 0; i < this.railBtns.length; i++) {
        var btn = this.railBtns[i];
        var on = btn.getAttribute('data-ib-mm-rail-letter') === letter;
        btn.classList[on ? 'add' : 'remove']('is-current');
      }
    },

    /*
      Scrolls the letter's header to just under the sticky panel head. A tap
      animates; a scrub (immediate) does not, because a smooth scroll per letter
      would queue up animations behind a finger that has already moved on.
    */
    jumpToLetter: function (letter, immediate) {
      var section = this.letterSection(letter);
      if (!section || !this.brandsPanel) return;
      if (section.hidden || !section.querySelector(LIVE_BRAND_ROW)) return;
      var top = section.offsetTop;
      if (typeof top !== 'number') return;
      this.scrollBrandsTo(Math.max(0, top - this.headHeight()), immediate);
      this.setCurrentLetter(letter);
    },

    /*
      Smooth where the browser and the shopper allow it. Older Safari throws on
      the options form of scrollTo, and reduced motion means jump - both land on
      the same plain scrollTop assignment.
    */
    scrollBrandsTo: function (top, immediate) {
      var panel = this.brandsPanel;
      if (!panel) return;

      if (!immediate && !prefersReducedMotion() && typeof panel.scrollTo === 'function') {
        try {
          panel.scrollTo({ top: top, behavior: 'smooth' });
          return;
        } catch (e) {
          /* fall through */
        }
      }
      panel.scrollTop = top;
    },

    headHeight: function () {
      if (!this.brandsPanel) return 0;
      var head = this.brandsPanel.querySelector(SELECTORS.head);
      var h = head ? head.offsetHeight : 0;
      return typeof h === 'number' ? h : 0;
    },

    // The sticky letter headers park under the sticky panel head, so the CSS
    // needs its measured height.
    syncHeadOffset: function () {
      if (!this.brandsPanel || !this.brandsPanel.style) return;
      var h = this.headHeight();
      if (!h || typeof this.brandsPanel.style.setProperty !== 'function') return;
      this.brandsPanel.style.setProperty('--ibm-head-h', h + 'px');
      this.syncRailPosition(h);
    },

    /*
      Centres the rail on the list, not on the window. top: 50% put it half way
      down the viewport, which is 80-odd pixels above the middle of the list -
      the top bar and the sticky head own that space - and it read as pinned to
      the top of the screen. Measured rather than calculated in CSS so iOS
      browser chrome, which grows and shrinks as you scroll, is accounted for.
    */
    syncRailPosition: function (headH) {
      if (!this.brandsPanel || !this.brandsPanel.style || !this.stage) return;
      if (typeof this.stage.getBoundingClientRect !== 'function') return;
      if (typeof this.brandsPanel.style.setProperty !== 'function') return;

      var box = this.stage.getBoundingClientRect();
      if (!box || !box.height) return;

      var head = typeof headH === 'number' ? headH : this.headHeight();
      var listTop = box.top + head;
      var listHeight = box.bottom - listTop;
      this.brandsPanel.style.setProperty('--ibm-rail-top', Math.round(listTop + listHeight / 2) + 'px');

      /*
        27 letters need ~485px. A small phone in landscape is still under the
        769px breakpoint, so the menu shows with barely 200px of list - the rail
        would run off both ends of the screen and its first letters could not be
        reached at all. Hide it there; the search field and plain scrolling
        still work. The natural height is remembered because a hidden rail
        measures zero, which would otherwise flip it straight back on.
      */
      if (!this.rail || typeof this.rail.getBoundingClientRect !== 'function') return;
      var railBox = this.rail.getBoundingClientRect();
      if (railBox && railBox.height) this.railNaturalHeight = railBox.height;
      if (!this.railNaturalHeight) return;
      this.rail.classList[this.railNaturalHeight > listHeight ? 'add' : 'remove']('is-cramped');
    },

    /*
      The rail sits quiet until the list moves, then brightens for a second and
      settles again. Scrolling a long list is exactly the moment a shortcut is
      worth noticing, and a control that answers the scroll reads as attached
      to it - no copy required.
    */
    wakeRail: function () {
      if (!this.rail || !this.rail.classList) return;
      var self = this;

      this.rail.classList.add('is-awake');
      if (this.railWakeTimer) clearTimeout(this.railWakeTimer);
      this.railWakeTimer = setTimeout(function () {
        self.railWakeTimer = null;
        if (self.rail && self.rail.classList) self.rail.classList.remove('is-awake');
      }, RAIL_WAKE_MS);
    },

    /*
      First time a shopper opens Brands, sweep the thumb once down the whole
      rail, top to bottom, then hand it back to wherever the list is. It shows
      that the rail is the whole alphabet and that the highlight moves; walking
      a few letters down and back again read as fidgeting. Every letter is a
      step, empty ones included - skipping them would make the thumb stutter
      across the gaps, and a steady beat is what reads as a sweep. Runs once
      ever (localStorage), never under reduced
      motion, never when the rail is hidden, and any real interaction cancels it
      mid-flight so it cannot fight the shopper for control.
    */
    maybeNudgeRail: function () {
      if (!this.rail || this.rail.hidden) return;
      if (this.rail.classList && this.rail.classList.contains('is-cramped')) return;
      if (!this.railBtns || this.railBtns.length < 4) return;
      if (prefersReducedMotion() || nudgeSeen()) return;

      // A rail with almost nothing live (a narrow search) has nothing to show.
      var live = 0;
      for (var i = 0; i < this.railBtns.length && live < 3; i++) {
        if (!this.railBtns[i].disabled) live += 1;
      }
      if (live < 3) return;

      // Marked up front: a nudge someone scrolled straight through still counts.
      markNudgeSeen();

      var steps = [];
      for (var j = 0; j < this.railBtns.length; j++) {
        steps.push(this.railBtns[j].getAttribute('data-ib-mm-rail-letter'));
      }
      var at = 0;
      var self = this;

      this.cancelNudge();
      this.nudging = true;

      var step = function () {
        if (!self.nudging) return;
        if (at >= steps.length) {
          self.endNudge();
          return;
        }
        self.wakeRail();
        self.setCurrentLetter(steps[at]);
        at += 1;
        self.nudgeTimer = setTimeout(step, NUDGE_STEP_MS);
      };

      this.nudgeTimer = setTimeout(step, NUDGE_DELAY_MS);
    },

    // Stops the walk, both halves of it - a caller should never have to
    // remember that "not nudging" is two properties.
    cancelNudge: function () {
      this.nudging = false;
      if (this.nudgeTimer) {
        clearTimeout(this.nudgeTimer);
        this.nudgeTimer = null;
      }
    },

    // Stops it and hands the highlight back to wherever the list actually is.
    // The flag has to be read before cancelNudge clears it.
    endNudge: function () {
      var wasNudging = this.nudging;
      this.cancelNudge();
      if (!wasNudging) return;
      this.syncCurrentLetterFromScroll();
    },

    /*
      Leaving the panel: nothing on it should still be animating. Both timers
      outlive the panel otherwise - the nudge keeps walking a rail nobody can
      see, and a wake left ticking greets the next visit mid-flash.
    */
    settleRail: function () {
      this.endNudge();
      if (this.railWakeTimer) {
        clearTimeout(this.railWakeTimer);
        this.railWakeTimer = null;
      }
      if (this.rail && this.rail.classList) this.rail.classList.remove('is-awake');
    },

    /*
      Keeps the rail's highlight on the letter the shopper is reading.

      The obvious rule - the last section whose top has passed the head - breaks
      at the end of the list: T through Z all fit on the final screen, so the
      rail would still say T while Z sits in plain view and the list cannot
      scroll any further. So the line we measure against slides with progress:
      it sits just under the head at the top of the list and at the bottom of
      the viewport once the list is scrolled to its end, which lands the
      highlight on the last letter exactly when the last letter is as far down
      the screen as it can get.
    */
    syncCurrentLetterFromScroll: function () {
      if (!this.brandsPanel || !this.brandLetters || !this.brandLetters.length) return;
      var panel = this.brandsPanel;
      var top = panel.scrollTop;
      if (typeof top !== 'number') return;

      var headH = this.headHeight();
      var edge = top + headH + 1;
      var view = panel.clientHeight;
      // A closed menu has no layout, so every section measures 0 and the last
      // one would win. Leave the highlight where it is until there is a list.
      if (typeof view === 'number' && view <= 0) return;
      var max = typeof panel.scrollHeight === 'number' && typeof view === 'number' ? panel.scrollHeight - view : 0;

      if (max > 0) {
        var progress = Math.min(1, Math.max(0, top / max));
        edge = top + headH + 1 + progress * Math.max(0, view - headH);
      }

      var current = '';
      for (var i = 0; i < this.brandLetters.length; i++) {
        var section = this.brandLetters[i];
        if (section.hidden || typeof section.offsetTop !== 'number') continue;
        if (section.offsetTop <= edge) current = section.getAttribute('data-ib-mm-letter');
      }
      this.setCurrentLetter(current);
    },

    // Rail highlight follows the scroll position. Root-scoped, so it dies with
    // the root; rAF keeps it off the scroll thread's critical path.
    bindBrandScroll: function () {
      if (!this.brandsPanel || typeof this.brandsPanel.addEventListener !== 'function') return;
      var self = this;
      var queued = false;

      this.brandsPanel.addEventListener('scroll', function () {
        if (queued) return;
        queued = true;
        var run = function () {
          queued = false;
          if (self.skipScrollWake) {
            self.skipScrollWake = false;
          } else {
            self.wakeRail();
            self.cancelNudge(); // the shopper got there first; the sync below restores the thumb
          }
          self.syncCurrentLetterFromScroll();
        };
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(run);
        else run();
      });
    },

    /*
      Drag the rail like the iOS Contacts index: the letter under the finger is
      the letter shown. elementFromPoint is what makes that work - touchmove
      keeps firing on the element the gesture STARTED on, so evt.target alone
      would report the first letter forever.
    */
    bindRailScrub: function () {
      if (!this.rail || typeof this.rail.addEventListener !== 'function') return;
      var self = this;
      var startY = null;
      var scrubbing = false;

      function pointOf(evt) {
        return evt.touches && evt.touches.length ? evt.touches[0] : evt;
      }

      /*
        Probe the rail's own centre line rather than the finger's x. The buttons
        are 13px wide and a thumb drifts inwards as it drags down, so trusting
        clientX would land on the brand list and stall the scrub - with touchmove
        still cancelled, the list would neither scrub nor scroll. Clamping y the
        same way keeps the first and last letters live when the finger runs past
        the ends of the rail.
      */
      function letterAt(evt) {
        var point = pointOf(evt);
        if (!point || typeof point.clientY !== 'number') return '';

        var x = point.clientX;
        var y = point.clientY;
        var rect = typeof self.rail.getBoundingClientRect === 'function' ? self.rail.getBoundingClientRect() : null;
        if (rect && rect.width) {
          x = rect.left + rect.width / 2;
          // Clamp to the letters, not the track: the track's ends are grip dots,
          // and a finger run past them should hold the first or last letter.
          var edges = self.railEdges(rect);
          y = Math.max(edges.top, Math.min(edges.bottom, y));
        }

        var el = typeof document.elementFromPoint === 'function' ? document.elementFromPoint(x, y) : evt.target;
        if (!el || typeof el.closest !== 'function') return '';
        var btn = el.closest(SELECTORS.railBtn);
        return btn ? btn.getAttribute('data-ib-mm-rail-letter') || '' : '';
      }

      function scrub(evt) {
        var letter = letterAt(evt);
        if (!letter || letter === self.scrubLetter) return;
        self.scrubLetter = letter;
        self.jumpToLetter(letter, true);
      }

      function end() {
        startY = null;
        scrubbing = false;
        self.scrubLetter = '';
      }

      // touchstart only arms the gesture. Scrubbing here would beat the tap's
      // click to the scroll position, so every tap would jump instead of
      // animating - the rail would have no smooth scroll at all on a phone.
      this.rail.addEventListener('touchstart', function (evt) {
        self.wakeRail();
        self.endNudge();
        var point = pointOf(evt);
        startY = point && typeof point.clientY === 'number' ? point.clientY : null;
        scrubbing = false;
        self.scrubLetter = '';
      }, TOUCH_OPTS);

      // The rail owns the vertical gesture; without this the panel scrolls too.
      this.rail.addEventListener('touchmove', function (evt) {
        if (evt.cancelable !== false && typeof evt.preventDefault === 'function') evt.preventDefault();

        if (!scrubbing) {
          var point = pointOf(evt);
          var y = point && typeof point.clientY === 'number' ? point.clientY : null;
          // Finger jitter during a tap is not a drag.
          if (startY !== null && y !== null && Math.abs(y - startY) < SCRUB_SLOP) return;
          scrubbing = true;
        }

        scrub(evt);
      }, TOUCH_OPTS);

      this.rail.addEventListener('touchend', end);
      this.rail.addEventListener('touchcancel', end);
    },

    bindBrandFilter: function () {
      if (!this.brandInput) return;
      var self = this;

      this.brandInput.addEventListener('input', function () {
        self.endNudge(); // typing is an answer; stop demonstrating
        if (self.filterTimer) clearTimeout(self.filterTimer);
        self.filterTimer = setTimeout(function () {
          self.filterTimer = null;
          self.applyBrandFilter(self.brandInput.value);
        }, FILTER_DEBOUNCE_MS);
      });

      // Some browsers fire 'search' (not 'input') for the native clear (x).
      this.brandInput.addEventListener('search', function () {
        self.applyBrandFilter(self.brandInput.value);
      });

      if (this.brandClear) {
        this.brandClear.addEventListener('click', function () {
          self.clearBrandFilter(true);
        });
      }
    },

    clearBrandFilter: function (refocus) {
      if (this.filterTimer) {
        clearTimeout(this.filterTimer);
        this.filterTimer = null;
      }
      if (this.brandInput) {
        this.brandInput.value = '';
        if (refocus) this.brandInput.focus();
      }
      this.applyBrandFilter('');
    },

    applyBrandFilter: function (raw) {
      if (!this.brandsPanel) return;
      // Not endNudge: its sync would measure the pre-filter layout, and the
      // sync after filtering restores the thumb anyway.
      this.cancelNudge();

      var query = normalizeText(raw);
      var visible = 0;
      var i;

      for (i = 0; i < this.brandRows.length; i++) {
        var row = this.brandRows[i];
        var match = matchesQuery(row.getAttribute('data-ib-mm-key') || '', query);
        var regionHidden = Boolean(row.getAttribute(REGION_HIDDEN_ATTR));
        // Only 'hidden' is ours; the region script's inline display:none stays.
        row.hidden = !match;
        if (match && !regionHidden) {
          visible += 1;
          row.setAttribute('data-ib-mm-visible', '1');
        } else {
          row.removeAttribute('data-ib-mm-visible');
        }
      }

      for (i = 0; i < this.brandLetters.length; i++) {
        this.brandLetters[i].hidden = !this.brandLetters[i].querySelector('[data-ib-mm-visible]');
      }
      for (i = 0; i < this.brandGroups.length; i++) {
        this.brandGroups[i].hidden = !this.brandGroups[i].querySelector('[data-ib-mm-visible]');
      }

      this.syncRail();
      this.syncColumns();
      // Letters come and go with the query, so the thumb has to be re-read
      // against what is left - otherwise it sits on a letter that is gone.
      this.syncCurrentLetterFromScroll();

      if (this.brandEmpty) this.brandEmpty.hidden = visible > 0;
      if (this.brandClear) this.brandClear.hidden = !query;
      if (this.brandCount) {
        this.brandCount.textContent = visible === 0
          ? 'No brands match'
          : visible + (visible === 1 ? ' brand' : ' brands');
      }
    }
  };

  /* ------------------------------------------------------------------------
     Public surface + boot
  ------------------------------------------------------------------------ */

  var api = {
    normalizeText: normalizeText,
    brandSortKey: brandSortKey,
    letterFor: letterFor,
    matchesQuery: matchesQuery,
    sortBrands: sortBrands,
    groupByLetter: groupByLetter,
    instance: null,
    open: function (evt) {
      if (api.instance) api.instance.open(evt);
    },
    close: function (immediate) {
      if (api.instance) api.instance.close(immediate);
    },
    goTo: function (id) {
      if (api.instance) api.instance.goTo(id);
    },
    back: function () {
      if (api.instance) api.instance.back();
    },
    destroy: function () {
      if (api.instance) api.instance.destroy();
      api.instance = null;
    }
  };

  window.IBMobileMenu = api;

  function boot() {
    var root = document.querySelector(SELECTORS.root);
    if (!root) return;
    if (root.getAttribute('data-ib-mm-portaled')) return; // already booted
    if (api.instance && typeof api.instance.destroy === 'function') api.instance.destroy();
    api.instance = new IBMobileMenu(root);
  }

  boot();
})();
